import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { Record } from 'pkg/api/models/record';

// Where a sent evaluation is: opening runs on its schedule, paused with no
// run booked, or ended for good, because its schedule has no date left or
// someone stopped it (`stoppedAt` tells which).
export enum CycleStatus {
	Active = 'active',
	Paused = 'paused',
	Ended = 'ended',
}

// The unit a cycle repeats in. A cycle that runs once has none.
export enum IntervalUnit {
	Week = 'week',
	Month = 'month',
}

// Whether and when a run's results reach players: when a coach publishes,
// at the deadline, or never, when the coaches keep them to themselves. Each
// run keeps the mode it opened with.
export enum PublishMode {
	Manual = 'manual',
	Automatic = 'automatic',
	Private = 'private',
}

// Who scores the people an evaluation reaches: the named raters, or the
// named raters and each person themselves.
export enum RatersMode {
	Coaches = 'coaches',
	CoachesAndPlayers = 'coaches_and_players',
}

// One evaluation inside a sent evaluation and who gets it. The ids are the
// intent: each run resolves the groups' players on the day it opens. An
// empty list comes back as null.
export interface Assignment extends Record {
	cycleId: number;
	evaluationId: number;
	groupIds: number[] | null;
	userIds: number[] | null;
	raterUserIds: number[] | null;
	ratersMode: RatersMode;
	// The column is the negative so that false, the default, lets a parent
	// answer the self-assessment for their child.
	parentsCannotAnswer: boolean;
	sortOrder: number;
}

// A sent evaluation, listed on the Sent tab. In code a cycle is the plan and
// each round it opens is a run; the UI calls each round "Cycle N".
export interface Cycle extends Record, Dateable, AllowedActionable {
	publicId: string;
	organizationId: number;
	// The group it was sent from.
	groupId: number;
	name: string;
	// Calendar dates, YYYY-MM-DD, in the sending group's timezone.
	startDate: string;
	intervalUnit: IntervalUnit | null;
	intervalCount: number;
	endAfterCount: number | null;
	endOnDate: string | null;
	// How many days each run stays open after the day it opens.
	openDays: number;
	publishMode: PublishMode;
	status: CycleStatus;
	// When someone stopped it with Stop sending, unix seconds. Null when it
	// ran out of dates instead, as a cycle sent once does the moment its
	// only run opens, and while it goes on. Absent from an API without it.
	stoppedAt?: number | null;
	// When the next run opens, unix seconds; null once nothing is left.
	nextOpenAt: number | null;
	runsOpened: number;
	// When the last scheduled date that opened nothing came, unix seconds:
	// nobody was left to evaluate, or no evaluation left to score. Such a
	// date is skipped and does not count toward "after N cycles". Null when
	// none was.
	skippedEmptyAt: number | null;
	createdByUserId: number | null;
	assignments: Assignment[];
	// The scheduler missed the next open date: offer "Open now".
	overdue: boolean;
	// The open time of the skipped date "Undo skip" would put back, unix
	// seconds; absent when there is nothing to undo.
	undoSkipAt?: number;
	// The open time of every date still to come that Skip passed over,
	// oldest first; undoSkipAt is the last. Absent when there is none, and
	// from an API without it.
	skippedAhead?: number[];
	// The deadline of its latest run still taking answers, unix seconds;
	// absent while none is.
	openUntil?: number;
}

// The schedule as the client sends it.
export interface SchedulePayload {
	startDate: string;
	intervalUnit: IntervalUnit | null;
	intervalCount: number;
	endAfterCount: number | null;
	endOnDate: string | null;
	openDays: number;
	publishMode: PublishMode;
}

// One evaluation and who gets it, as the client sends it.
export interface AssignmentPayload {
	evaluationPublicId: string;
	groupIds: number[];
	userIds: number[];
	raterUserIds: number[];
	ratersMode: RatersMode;
	parentsCannotAnswer: boolean;
}

export interface CreatePayload {
	// Left empty, the API names it after the group and the evaluations.
	name: string;
	schedule: SchedulePayload;
	assignments: AssignmentPayload[];
}

// Only what is sent changes; a sent list of assignments replaces the whole
// list. Every change applies from the next run.
export type UpdatePayload = Partial<CreatePayload>;

// One run a schedule would open, as the confirm step lists it.
export interface PreviewRun {
	number: number;
	opensAt: number;
	closesAt: number;
}

// Whom one rater would rate, as the confirm step says it: everyone the
// evaluation reaches, or the players of the chosen groups listed, with
// anyone named by hand. Neither, for someone the send would refuse.
export interface PreviewRater {
	userId: number;
	everyone: boolean;
	groupIds: number[];
}

// One assignment's raters, in the order sent.
export interface AssignmentRaters {
	raters: PreviewRater[];
}

// One assignment's groups and raters, as the confirm step asks about them.
export interface RatersPayload {
	groupIds: number[];
	raterUserIds: number[];
}
